import { z } from 'zod';
import { estimatingEstimateLineInputSchema, estimatingMoneyCentsSchema, estimatingQuantityTenThousandthsSchema, } from './estimating-estimate.contract.js';
import { estimatingPricebookItemIdSchema } from './estimating-pricebook.contract.js';
export const estimatingEstimateTemplatesCapability = 'estimating.estimate-templates.v1';
export const estimatingEstimateTemplateVersionsCapability = 'estimating.estimate-template-versions.v1';
export const estimatingEstimateTemplateApplicationCapability = 'estimating.estimate-template-application.v1';
export const estimatingEstimateTemplateCategorySchema = z.enum([
    'mitigation', 'mold', 'fire', 'carpet_cleaning', 'biohazard',
    'reconstruction', 'contents', 'other',
]);
export const estimatingEstimateTemplateScalingBasisSchema = z.enum([
    'square-feet', 'linear-feet', 'rooms', 'openings', 'flat',
]);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const reason = z.string().trim().min(10).max(500);
export const estimatingEstimateTemplateScalingSchema = z.strictObject({
    basis: estimatingEstimateTemplateScalingBasisSchema,
    perTenThousandths: estimatingQuantityTenThousandthsSchema.nullable().default(null),
    minimumQuantityTenThousandths: estimatingQuantityTenThousandthsSchema.default(10_000),
    multiplyByDays: z.boolean().default(false),
}).superRefine((value, context) => {
    if (value.basis === 'flat' && value.perTenThousandths !== null) {
        context.addIssue({ code: 'custom', path: ['perTenThousandths'], message: 'Flat scaling cannot use a per value.' });
    }
    if (value.basis !== 'flat' && value.perTenThousandths === null) {
        context.addIssue({ code: 'custom', path: ['perTenThousandths'], message: 'Measured scaling requires a per value.' });
    }
});
export const estimatingEstimateTemplateLineInputSchema = z.strictObject({
    lineKey: z.string().uuid(),
    pricebookItemId: estimatingPricebookItemIdSchema.nullable().default(null),
    groupLabel: nullableText(120).default(null),
    name: z.string().trim().min(1).max(240),
    description: nullableText(4_000).default(null),
    savedQuantityTenThousandths: estimatingQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    savedUnitPriceCents: estimatingMoneyCentsSchema,
    taxable: z.boolean().default(true),
    optional: z.boolean().default(false),
    scaling: estimatingEstimateTemplateScalingSchema.nullable().default(null),
});
export const estimatingEstimateTemplateDraftSchema = z.strictObject({
    name: z.string().trim().min(1).max(120),
    description: nullableText(500).default(null),
    customerMessage: nullableText(2_000).default(null),
    internalNotes: nullableText(4_000).default(null),
    category: estimatingEstimateTemplateCategorySchema.nullable().default(null),
    sourceTemplateId: z.string().uuid().nullable().default(null),
    sourceTemplateVersionId: z.string().uuid().nullable().default(null),
    lines: z.array(estimatingEstimateTemplateLineInputSchema).min(1).max(100),
}).superRefine((value, context) => {
    if ((value.sourceTemplateId === null) !== (value.sourceTemplateVersionId === null)) {
        context.addIssue({ code: 'custom', path: ['sourceTemplateId'], message: 'Template copy provenance must be complete.' });
    }
});
export const estimatingEstimateTemplateVersionSchema = z.strictObject({
    contractVersion: z.literal(estimatingEstimateTemplateVersionsCapability),
    tenantId: z.string().uuid(), id: z.string().uuid(), templateId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/), reason,
    actorMembershipId: z.string().uuid(), createdAt: z.string().datetime({ offset: true }),
    data: estimatingEstimateTemplateDraftSchema,
});
export const estimatingEstimateTemplateSchema = z.strictObject({
    contractVersion: z.literal(estimatingEstimateTemplatesCapability),
    tenantId: z.string().uuid(), id: z.string().uuid(), revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(), archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(), updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }), updatedAt: z.string().datetime({ offset: true }),
    currentVersion: estimatingEstimateTemplateVersionSchema,
});
export const estimatingCreateEstimateTemplateCommandSchema = z.strictObject({
    kind: z.literal('create'), commandId: z.string().uuid(), templateId: z.string().uuid(),
    template: estimatingEstimateTemplateDraftSchema, reason,
});
export const estimatingEstimateTemplateCommandSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('revise'), commandId: z.string().uuid(), expectedRevision: z.number().int().positive(),
        template: estimatingEstimateTemplateDraftSchema, reason,
    }),
    z.strictObject({
        kind: z.enum(['archive', 'restore']), commandId: z.string().uuid(),
        expectedRevision: z.number().int().positive(), reason,
    }),
]);
export const estimatingEstimateTemplateListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    category: estimatingEstimateTemplateCategorySchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const estimatingEstimateTemplateApplicationInputSchema = z.strictObject({
    squareFeetTenThousandths: z.number().int().min(1).max(10_000_000_000).nullable().default(null),
    linearFeetTenThousandths: z.number().int().min(1).max(10_000_000_000).nullable().default(null),
    rooms: z.number().int().positive().max(10_000).nullable().default(null),
    openings: z.number().int().positive().max(100_000).nullable().default(null),
    days: z.number().int().positive().max(10_000).nullable().default(null),
});
const commandReceipt = z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() });
export const estimatingEstimateTemplateResponseSchema = z.strictObject({
    data: estimatingEstimateTemplateSchema, command: commandReceipt.optional(),
});
export const estimatingEstimateTemplatesResponseSchema = z.strictObject({
    data: z.array(estimatingEstimateTemplateSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const estimatingEstimateTemplateVersionsResponseSchema = z.strictObject({
    data: z.array(estimatingEstimateTemplateVersionSchema),
});
export const estimatingEstimateTemplateApplicationResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(estimatingEstimateTemplateApplicationCapability),
        templateId: z.string().uuid(), templateVersionId: z.string().uuid(),
        name: z.string().trim().min(1).max(120),
        customerMessage: nullableText(2_000), internalNotes: nullableText(4_000),
        lines: z.array(estimatingEstimateLineInputSchema),
        ratesRefreshed: z.number().int().nonnegative(),
        quantitiesScaled: z.number().int().nonnegative(),
        missingPricebookItemIds: z.array(estimatingPricebookItemIdSchema),
    }),
});
function divideCeiling(numerator, denominator) {
    return (numerator + denominator - 1n) / denominator;
}
export function calculateEstimatingTemplateQuantity(scaling, savedQuantityTenThousandths, inputs) {
    if (scaling === null)
        return savedQuantityTenThousandths;
    let quantity = BigInt(savedQuantityTenThousandths);
    if (scaling.basis !== 'flat') {
        const basisValue = scaling.basis === 'square-feet'
            ? inputs.squareFeetTenThousandths
            : scaling.basis === 'linear-feet'
                ? inputs.linearFeetTenThousandths
                : scaling.basis === 'rooms'
                    ? inputs.rooms === null ? null : inputs.rooms * 10_000
                    : inputs.openings === null ? null : inputs.openings * 10_000;
        if (basisValue !== null && scaling.perTenThousandths !== null) {
            const wholeUnits = divideCeiling(BigInt(basisValue), BigInt(scaling.perTenThousandths));
            quantity = wholeUnits * 10000n;
            if (quantity < BigInt(scaling.minimumQuantityTenThousandths)) {
                quantity = BigInt(scaling.minimumQuantityTenThousandths);
            }
        }
    }
    if (scaling.multiplyByDays && inputs.days !== null)
        quantity *= BigInt(inputs.days);
    if (quantity > 10000000n)
        throw new RangeError('Scaled template quantity exceeds the estimate limit.');
    return Number(quantity);
}
//# sourceMappingURL=estimating-estimate-template.contract.js.map